import { useCallback, useMemo, useState } from "react"
import {
  METRIC_CATALOG,
  calculatedMetricCatalogEntries,
  resolveCatalogMetrics,
  sourcesForMetricKeys,
  type CalculatedMetricDef,
  type MetricCatalogEntry,
} from "~/shared/kpi"
import type { AggregationType } from "~/ui-rtk/constants/charts"
import MultiSelectWithChips from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import { type TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import DropdownMenu from "~/ui-rtk/components/ui/controls/DropdownMenu/DropdownMenu"
import AnimatedContainer from "~/ui-rtk/components/ui/controls/AnimatedButton/AnimatedButton"
import { DateRangePicker } from "~/ui-rtk/components/ui/controls"
import SegmentedToggle from "~/ui-rtk/components/ui/controls/SegmentedToggle/SegmentedToggle"
import {
  getMetricsChartPresets,
  type TAllMetricsChartsKey,
} from "~/ui-rtk/components/features/dashboards/BrandValueOverviewDashboard/constants/all-metrics-chart-presets"
import {
  metricEntryFor,
  useMetricDictionary,
} from "~/ui-rtk/hooks/metric-dictionary"
import { useUrlParamsStateArray } from "~/ui-rtk/hooks/urlParamsState"
import { getLightColor, stringToColor } from "~/ui-rtk/utils/chart-utils"
import KPIGroupChart, { type ChartVisualization } from "./KPIGroupChart"
import type { ColumnBucket, KpiRow } from "../utils/custom-metric-rows"
import type { KpiWindows } from "../utils/kpi-compare-date-range"
import { localDateFromStr, type ExplorerRange } from "../utils/explorer-compare"

// The unified Metrics Explorer: chart ANY metric from the KPI catalog (all
// seven sources — cube metrics, brand value, media campaigns, computed,
// the ACTIVE report's calculated metrics, custom manual-entry, initiative
// breakouts) as line/bar time series. Rows come from the page's ONE data
// pipeline (buildCustomGroupData over the shared useConnect datasets, typed
// folds included), so every plotted value matches the dashboard tables
// exactly.

/** URL param carrying the selected metric keys (BrandValue explorer precedent). */
export const EXPLORER_SELECTED_METRICS_PARAM = "csm"

const VISUALIZATION_OPTIONS: Array<{
  label: string
  value: ChartVisualization
}> = [
  { label: "Line", value: "line" },
  { label: "Bar", value: "bar" },
]

/** Per-source loading flags, mirroring the custom-group gate in index.tsx. */
export interface ExplorerSourceLoading {
  base: boolean
  brand_value: boolean
  media_campaigns: boolean
  initiatives: boolean
  computed: boolean
  custom: boolean
}

export interface ExplorerViewProps {
  granularity: AggregationType
  /** The page-level Show-YoY toggle (comparison also shows when a compare range is active). */
  showYoY: boolean
  /** The pipeline's resolved fetch windows — the picker's default range. */
  windows: KpiWindows
  /** The user's picked windows; null = untouched (default trailing window). */
  range: ExplorerRange | null
  onRangeChange: (range: ExplorerRange) => void
  /** The ACTIVE report's calculated-metric defs (agreed product scoping). */
  calcDefs: CalculatedMetricDef[]
  /** Per-render synthesized entries (custom.<id> + initiative.CUSTOM_*). */
  catalogExtraEntries: MetricCatalogEntry[]
  columnBuckets: ColumnBucket[]
  buildCustomGroupData: (
    metricKeys: string[],
    calcDefs: CalculatedMetricDef[],
  ) => KpiRow[]
  sourceLoading: ExplorerSourceLoading
  amazonEnabled: boolean
  isGaMultiPropertyEnabled: boolean
}

export default function ExplorerView({
  granularity,
  showYoY,
  windows,
  range,
  onRangeChange,
  calcDefs,
  catalogExtraEntries,
  columnBuckets,
  buildCustomGroupData,
  sourceLoading,
  amazonEnabled,
  isGaMultiPropertyEnabled,
}: ExplorerViewProps) {
  const { metricMap } = useMetricDictionary()
  const [visualization, setVisualization] = useState<ChartVisualization>("line")

  // Full pickable catalog: the frozen METRIC_CATALOG plus the page's
  // per-render synthesized entries and the active report's calc metrics.
  const catalogEntries = useMemo(
    () => [
      ...METRIC_CATALOG,
      ...catalogExtraEntries,
      ...calculatedMetricCatalogEntries(calcDefs),
    ],
    [catalogExtraEntries, calcDefs],
  )

  // Drops keys that resolve nowhere (stale URL params, preset keys outside
  // the catalog) — the same resolution path the chart itself uses.
  const filterToCatalog = useCallback(
    (keys: string[]): string[] =>
      resolveCatalogMetrics(keys, calcDefs, catalogExtraEntries).map(
        metric => metric.key,
      ),
    [calcDefs, catalogExtraEntries],
  )

  // Picker options grouped by catalog category; titles overridden from the
  // Metric Dictionary (single source of truth — same pattern as the Brand
  // Value explorer), colors seeded by key so chips match the chart series.
  const options = useMemo(
    () =>
      catalogEntries.map(entry => ({
        label: metricEntryFor(metricMap, entry.key)?.title ?? entry.label,
        value: entry.key,
        group: entry.category,
        color: getLightColor(stringToColor(entry.key)),
      })),
    [catalogEntries, metricMap],
  )

  const groups = useMemo(() => {
    const seen = new Set<string>()
    const out: Array<{ label: string; value: string }> = []
    for (const entry of catalogEntries) {
      if (seen.has(entry.category)) continue
      seen.add(entry.category)
      out.push({ label: entry.category, value: entry.category })
    }
    return out
  }, [catalogEntries])

  const presets = useMemo(
    () => getMetricsChartPresets(amazonEnabled, isGaMultiPropertyEnabled),
    [amazonEnabled, isGaMultiPropertyEnabled],
  )

  // Resolved against the FROZEN catalog only — preset keys are always plain
  // metrics.* entries, so calc defs / extra entries can never affect the
  // result, and depending on them (via filterToCatalog) would churn this
  // array's identity on every report switch and re-derive the unset-URL-param
  // selection state through every downstream memo.
  const defaultSelection = useMemo(
    () =>
      resolveCatalogMetrics(
        presets.BASELINE_REVENUE_BRAND_VALUE.metrics,
        [],
        [],
      ).map(metric => metric.key),
    [presets],
  )

  const { state: selectedKeys, setState: setSelectedKeys } =
    useUrlParamsStateArray<Array<string>>(
      EXPLORER_SELECTED_METRICS_PARAM,
      defaultSelection,
    )

  const selectedOptions = useMemo(
    () =>
      options.filter(({ value }) => (selectedKeys as string[]).includes(value)),
    [options, selectedKeys],
  )

  const presetOptions = useMemo(
    () => Object.values(presets).map(({ label, value }) => ({ label, value })),
    [presets],
  )

  const handleSetPreset = useCallback(
    (presetId: string | number) => {
      const preset = presets[presetId as TAllMetricsChartsKey]
      if (!preset) return
      setSelectedKeys(filterToCatalog(preset.metrics))
    },
    [presets, filterToCatalog, setSelectedKeys],
  )

  // Rows from the SAME pipeline the tables render (folds, dictionary labels,
  // typed-row insertions included) — exact value parity with the dashboard.
  const rows = useMemo(
    () => buildCustomGroupData(selectedKeys as string[], calcDefs),
    [buildCustomGroupData, selectedKeys, calcDefs],
  )

  const metricDefs = useMemo(
    () =>
      resolveCatalogMetrics(
        selectedKeys as string[],
        calcDefs,
        catalogExtraEntries,
      ),
    [selectedKeys, calcDefs, catalogExtraEntries],
  )

  // Gate on the base metrics query unconditionally — it supplies the column
  // buckets every chart needs — plus whichever OTHER sources the selection
  // actually reads (calc keys expand to their leaf sources), mirroring the
  // custom-group gate.
  const isLoading = useMemo(() => {
    const groupSources = sourcesForMetricKeys(
      selectedKeys as string[],
      calcDefs,
    )
    return (
      sourceLoading.base ||
      (groupSources.has("brand_value") && sourceLoading.brand_value) ||
      (groupSources.has("media_campaigns") && sourceLoading.media_campaigns) ||
      (groupSources.has("initiatives") && sourceLoading.initiatives) ||
      (groupSources.has("computed") && sourceLoading.computed) ||
      (groupSources.has("custom") && sourceLoading.custom)
    )
  }, [selectedKeys, calcDefs, sourceLoading])

  // Picker display: the picked range, else the pipeline's default trailing
  // window for the current granularity.
  const pickerStart =
    range?.dateRange[0] ?? localDateFromStr(windows.currentRange[0])
  const pickerEnd =
    range?.dateRange[1] ?? localDateFromStr(windows.currentRange[1])

  const handlePeriodChange = useCallback(
    ({
      start,
      end,
      compareStart,
      compareEnd,
    }: {
      start: Date
      end: Date
      compareStart?: Date | null
      compareEnd?: Date | null
    }) => {
      onRangeChange({
        dateRange: [start, end],
        ...(compareStart && compareEnd
          ? { compareRange: [compareStart, compareEnd] as [Date, Date] }
          : {}),
      })
    },
    [onRangeChange],
  )

  return (
    <div>
      <div className="mb-6 flex items-start gap-system-sm">
        <div className="h-full flex-shrink-0">
          <DropdownMenu
            className="relative inline-block p-0"
            options={presetOptions}
            onItemClick={handleSetPreset}
          >
            <AnimatedContainer>
              <span className="flex items-center gap-system-xs">
                Select Preset
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-system-md w-system-md"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </span>
            </AnimatedContainer>
          </DropdownMenu>
        </div>
        <div className="min-w-0 flex-1">
          <MultiSelectWithChips
            options={options}
            groups={groups}
            showTypeAhead
            value={selectedOptions}
            onChange={(picked: TMultiSelectOption[]) =>
              setSelectedKeys(picked.map(({ value }) => value))
            }
          />
        </div>
        <div className="flex-shrink-0">
          <DateRangePicker
            start={pickerStart}
            end={pickerEnd}
            granularity={granularity}
            comparisonEnabled
            compareStart={range?.compareRange?.[0]}
            compareEnd={range?.compareRange?.[1]}
            onChange={handlePeriodChange}
          />
        </div>
      </div>

      <KPIGroupChart
        title="Metrics Explorer"
        sizeKey="explorer"
        rows={rows}
        columnBuckets={columnBuckets}
        metricDefs={metricDefs}
        visualization={visualization}
        granularity={granularity}
        // A custom range always carries a compare window (user-picked or the
        // derived year-back one), so its series are always meaningful to show.
        showYoY={showYoY || range !== null}
        isLoading={isLoading}
        headerControls={
          <SegmentedToggle
            variant="joined"
            options={VISUALIZATION_OPTIONS}
            value={visualization}
            onChange={value => setVisualization(value as ChartVisualization)}
          />
        }
      />
    </div>
  )
}
